import { useState } from "react";
import { MessageSquare, Send, X } from "lucide-react";
import { useLocation } from "wouter";
import { useSubmitFeedback } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [location] = useLocation();
  const { toast } = useToast();

  const mutation = useSubmitFeedback({
    mutation: {
      onSuccess: () => {
        toast({ title: "Bedankt!", description: "Je feedback is verzonden." });
        setMessage("");
        setOpen(false);
      },
      onError: () => {
        toast({ title: "Fout", description: "Kon feedback niet verzenden. Probeer het opnieuw.", variant: "destructive" });
      },
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;
    mutation.mutate({ data: { message: trimmed, page: location } });
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[98]"
          onClick={() => setOpen(false)}
        />
      )}

      <div className="fixed bottom-6 left-6 z-[99]" data-testid="feedback-widget">
        {open && (
          <div
            className="absolute bottom-14 left-0 w-80 bg-card border border-border rounded-2xl shadow-xl p-4 animate-in slide-in-from-bottom-2 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-popover"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Feedback geven</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Vertel ons wat je ervan vindt..."
                maxLength={2000}
                rows={4}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                data-testid="feedback-textarea"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">
                  {message.length}/2000
                </span>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!message.trim() || mutation.isPending}
                  data-testid="feedback-submit"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {mutation.isPending ? "Verzenden..." : "Verstuur"}
                </Button>
              </div>
            </form>
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all hover:-translate-y-0.5 flex items-center justify-center"
          aria-label="Feedback geven"
          data-testid="feedback-toggle"
        >
          <MessageSquare className="w-5 h-5" />
        </button>
      </div>
    </>
  );
}
